<script lang="ts">
const MODAL_NAME = 'common:upsell:generic-modal'
</script>
<script setup lang="ts">
    import type { Benefit } from '~/app/auth/enums/TenantSubscriptionPlan';
    import type { UpsellTriggerType } from '~/composables/useUpsellAccess';

    

    const benefit = ref<Benefit>();
    const permissionKey = ref<string>();
    const title = ref<string>();
    const description = ref<string>();
    const crossFamily = ref<boolean>(false);

    const { getUpsellTrackingPlan, trackUpsellRestriction } = useUpsellAccess();
    const { $tracker } = useNuxtApp();
    // True when the modal closes because of a route change, so we don't report that
    // automatic close as a user dismiss.
    const closingFromRouteChange = ref(false);

    useModal(MODAL_NAME, (payload?: { benefit?: Benefit; permissionKey?: string; title?: string; description?: string; crossFamily?: boolean }) => {
        if (payload) {
            benefit.value = payload.benefit;
            permissionKey.value = payload.permissionKey;
            title.value = payload.title;
            description.value = payload.description;
            crossFamily.value = payload.crossFamily ?? false;
        }
    });

    useListen('upsell:modal:open', (payload: { benefit?: Benefit; permissionKey?: string; title?: string; description?: string; type?: UpsellTriggerType; label?: string; crossFamily?: boolean }) => {
        benefit.value = payload.benefit;
        permissionKey.value = payload.permissionKey;
        title.value = payload.title;
        description.value = payload.description;
        crossFamily.value = payload.crossFamily ?? false;
        useEvent('modal:open', MODAL_NAME);

        // The modal is the single tracking point for click-triggered upsells (sidebar nav,
        // action menu). Surfaces that render inline (page / panel / tabs) track themselves.
        trackUpsellRestriction(payload.type ?? 'nav', { permissionKey: payload.permissionKey, benefit: payload.benefit, label: payload.label });
    });

    const onClosed = () => {
        if (closingFromRouteChange.value) {
            closingFromRouteChange.value = false;
            return;
        }
        const { planId, commercialName } = getUpsellTrackingPlan({ permissionKey: permissionKey.value, benefit: benefit.value });
        $tracker.track('upsell_modal_closed', { plan_id: planId, commercial_name: commercialName });
    };

    // Close modal on route change
    const route = useRoute();
    watch(
        () => route.fullPath,
        () => {
            closingFromRouteChange.value = true;
            useEvent('modal:close', MODAL_NAME);
        }
    );
</script>

<template>
    <Modal
        :name="MODAL_NAME"
        :width="1000"
        content-classes="!p-0"
        @closed="onClosed"
    >
        <CommonUpsellContent
            v-if="benefit || permissionKey"
            :benefit="benefit"
            :permission-key="permissionKey"
            :title="title"
            :description="description"
            :cross-family="crossFamily"
        />
    </Modal>
</template>
